import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "@/components/ui/Modal";
import { useDeliverables, usePhases } from "@/hooks/useLive";
import type { Artifact } from "@/lib/types";
import { Search, X } from "lucide-react";

const inputCls = "w-full rounded-md border border-border bg-background px-3 py-2 text-body text-foreground focus:border-primary focus:outline-none";
const labelCls = "mb-1 block text-caption font-semibold text-muted-foreground";
const req = <span className="text-destructive">*</span>;

function Actions({ saving, onClose, submitLabel }: { saving: boolean; onClose: () => void; submitLabel: string }) {
  return (
    <div className="flex justify-end gap-2 pt-1">
      <button type="button" onClick={onClose} className="rounded-md border border-border px-4 py-2 text-caption text-muted-foreground hover:text-foreground">Cancel</button>
      <button type="submit" disabled={saving} className="rounded-md bg-primary px-4 py-2 text-caption font-medium text-white hover:bg-primary/90 disabled:opacity-50">
        {saving ? "Saving…" : submitLabel}
      </button>
    </div>
  );
}

function ErrorBanner({ error }: { error: string }) {
  if (!error) return null;
  return <div className="rounded-md bg-destructive/10 px-3 py-2 text-caption text-destructive">{error}</div>;
}

async function postJson(url: string, body: unknown) {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed" }));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

async function patchJson(url: string, body: unknown) {
  const res = await fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: "Failed" }));
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json();
}

/* ─────────── Task ─────────── */
export function CreateDeliverableModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: "", owner: "", phase: "", due: "",
    category: "systems", status: "not_started", flag: "", dependencies: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError("");
    try {
      await postJson("/api/deliverables", form);
      qc.invalidateQueries({ queryKey: ["deliverables"] });
      setForm({ name: "", owner: "", phase: "", due: "", category: "systems", status: "not_started", flag: "", dependencies: "", notes: "" });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add a task">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Task name {req}</label>
          <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="e.g. Draft pricing framework" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Owner</label>
            <input value={form.owner} onChange={(e) => set("owner", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Due</label>
            <input value={form.due} onChange={(e) => set("due", e.target.value)} className={inputCls} placeholder="May 4" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Category</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              <option value="messaging">Messaging & Positioning</option>
              <option value="pricing">Pricing & Packaging</option>
              <option value="enablement">Training & Enablement</option>
              <option value="demo">Demo & Proof</option>
              <option value="design">Design & Website</option>
              <option value="marketing">Marketing & Demand</option>
              <option value="cs">Onboarding & CX</option>
              <option value="systems">Internal Systems & Tracking</option>
              <option value="billing">Billing & Subscription</option>
              <option value="deal_exec">Deal Execution</option>
              <option value="people_comp">People, Comp & Org</option>
              <option value="pipeline_ops">Pipeline & RevOps</option>
              <option value="outbound">Outbound & Felix Coverage</option>
              <option value="post_launch">Post-Launch</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
              <option value="not_started">Not Started</option>
              <option value="in_progress">In Progress</option>
              <option value="at_risk">At Risk</option>
              <option value="blocked">Blocked</option>
              <option value="complete">Complete</option>
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Phase</label>
          <select value={form.phase} onChange={(e) => set("phase", e.target.value)} className={inputCls}>
            <option value="">— auto-derive from due date —</option>
            <option value="Alpha+">Alpha+</option>
            <option value="Closed Beta 1">Closed Beta 1</option>
            <option value="Closed Beta 2">Closed Beta 2</option>
            <option value="GA">GA</option>
            <option value="Post-GA">Post-GA</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Flag (optional)</label>
          <input value={form.flag} onChange={(e) => set("flag", e.target.value)} className={inputCls} placeholder="Longest pole…" />
        </div>
        <div>
          <label className={labelCls}>Notes</label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} />
        </div>
        <p className="text-micro text-muted-foreground">
          Attach links, docs, or assets from the <strong>Assets</strong> page after creating this task.
        </p>
        <ErrorBanner error={error} />
        <Actions saving={saving} onClose={onClose} submitLabel="Add task" />
      </form>
    </Modal>
  );
}

/* ─────────── Asset (artifact) ─────────── */
const ARTIFACT_KINDS: { value: string; label: string }[] = [
  { value: "doc", label: "Doc" },
  { value: "slide", label: "Slides / Deck" },
  { value: "sheet", label: "Sheet" },
  { value: "figma", label: "Figma" },
  { value: "loom", label: "Loom" },
  { value: "video", label: "Video" },
  { value: "image", label: "Image" },
  { value: "other", label: "Other" },
];

interface AssetFormProps {
  open: boolean;
  onClose: () => void;
  initial?: Artifact;
  defaultTaskId?: string;
}

function AssetForm({ open, onClose, initial, defaultTaskId }: AssetFormProps) {
  const qc = useQueryClient();
  const tasks = useDeliverables();
  const isEdit = !!initial;

  const [form, setForm] = useState(() => ({
    name: initial?.name ?? "",
    link: initial?.link ?? "",
    kind: initial?.kind ?? "doc",
    owner: initial?.owner ?? "",
    notes: initial?.notes ?? "",
  }));
  const [taskIds, setTaskIds] = useState<string[]>(() => {
    if (initial?.taskIds?.length) return initial.taskIds;
    return defaultTaskId ? [defaultTaskId] : [];
  });
  const [taskQuery, setTaskQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset on open/initial change
  useEffect(() => {
    if (!open) return;
    setForm({
      name: initial?.name ?? "",
      link: initial?.link ?? "",
      kind: initial?.kind ?? "doc",
      owner: initial?.owner ?? "",
      notes: initial?.notes ?? "",
    });
    setTaskIds(initial?.taskIds?.length ? initial.taskIds : (defaultTaskId ? [defaultTaskId] : []));
    setTaskQuery("");
    setError("");
  }, [open, initial, defaultTaskId]);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const tasksById = useMemo(() => {
    const m = new Map(tasks.map((t) => [String(t.id), t]));
    return m;
  }, [tasks]);

  const taskMatches = useMemo(() => {
    if (!taskQuery.trim()) return [];
    const q = taskQuery.toLowerCase();
    const selected = new Set(taskIds);
    return tasks
      .filter((t) => !selected.has(String(t.id)))
      .filter((t) => `${t.name} ${t.owner}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [tasks, taskQuery, taskIds]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!form.name.trim()) { setError("Name is required."); return; }
    if (!form.link.trim()) { setError("A link is required — an asset is something you can open."); return; }
    setSaving(true); setError("");
    try {
      const body = { ...form, taskIds };
      if (isEdit) {
        await patchJson(`/api/artifacts/${initial!.id}`, body);
      } else {
        await postJson("/api/artifacts", body);
      }
      qc.invalidateQueries({ queryKey: ["artifacts"] });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? "Edit asset" : "Add an asset"}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Name {req}</label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            className={inputCls}
            placeholder="e.g. Beta onboarding deck v2"
          />
        </div>
        <div>
          <label className={labelCls}>Link {req}</label>
          <input
            type="url"
            value={form.link}
            onChange={(e) => set("link", e.target.value)}
            className={inputCls}
            placeholder="https://docs.google.com/… or Figma / Loom / etc."
          />
        </div>
        <div>
          <label className={labelCls}>Linked tasks <span className="text-muted-foreground">(optional — can attach to multiple)</span></label>
          {taskIds.length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {taskIds.map((tid) => {
                const t = tasksById.get(String(tid));
                return (
                  <span key={tid} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-micro text-foreground">
                    <span className="max-w-[18rem] truncate">{t ? t.name : `#${tid}`}</span>
                    <button type="button" onClick={() => setTaskIds((s) => s.filter((x) => x !== tid))} className="text-muted-foreground hover:text-destructive">
                      <X size={11} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
          <div className="relative">
            <Search size={13} className="pointer-events-none absolute left-2.5 top-2.5 text-muted-foreground" />
            <input
              value={taskQuery}
              onChange={(e) => setTaskQuery(e.target.value)}
              placeholder="Search tasks to attach…"
              className="h-9 w-full rounded-md border border-border bg-background pl-7 pr-3 text-caption focus:border-primary focus:outline-none"
            />
            {taskMatches.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-card shadow-card-hover">
                {taskMatches.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setTaskIds((s) => [...s, String(t.id)]); setTaskQuery(""); }}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-caption hover:bg-accent"
                  >
                    <span className="truncate">{t.name}</span>
                    <span className="shrink-0 text-micro text-muted-foreground">{t.owner}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Kind</label>
            <select value={form.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls}>
              {ARTIFACT_KINDS.map((k) => (
                <option key={k.value} value={k.value}>{k.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Owner</label>
            <input value={form.owner} onChange={(e) => set("owner", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Notes</label>
          <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} />
        </div>
        <ErrorBanner error={error} />
        <Actions saving={saving} onClose={onClose} submitLabel={isEdit ? "Save changes" : "Add asset"} />
      </form>
    </Modal>
  );
}

export function CreateArtifactModal({ open, onClose, defaultTaskId }: { open: boolean; onClose: () => void; defaultTaskId?: string }) {
  return <AssetForm open={open} onClose={onClose} defaultTaskId={defaultTaskId} />;
}

export function EditArtifactModal({ open, onClose, artifact }: { open: boolean; onClose: () => void; artifact: Artifact | null }) {
  if (!artifact) return null;
  return <AssetForm open={open} onClose={onClose} initial={artifact} />;
}

/* ─────────── Risk ─────────── */
export function CreateRiskModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const PHASES = usePhases();
  const [form, setForm] = useState({ title: "", severity: "medium", owner: "", detail: "", status: "open", phase: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError("Title is required."); return; }
    setSaving(true); setError("");
    try {
      await postJson("/api/risks", form);
      qc.invalidateQueries({ queryKey: ["risks"] });
      setForm({ title: "", severity: "medium", owner: "", detail: "", status: "open", phase: "" });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log a risk or flag">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Title {req}</label>
          <input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} placeholder="e.g. Twilio approval slipping past Apr 27" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Severity {req}</label>
            <select value={form.severity} onChange={(e) => set("severity", e.target.value)} className={inputCls}>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Owner</label>
            <input value={form.owner} onChange={(e) => set("owner", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Detail</label>
          <textarea value={form.detail} onChange={(e) => set("detail", e.target.value)} rows={3} className={inputCls} placeholder="What's the risk, and what would unblock it?" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Phase</label>
            <select value={form.phase} onChange={(e) => set("phase", e.target.value)} className={inputCls}>
              <option value="">— Not tied to a phase —</option>
              {PHASES.map((p) => (
                <option key={p.id} value={p.name}>{p.name}{p.isGate ? " (gate)" : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
        </div>
        <ErrorBanner error={error} />
        <Actions saving={saving} onClose={onClose} submitLabel="Log risk" />
      </form>
    </Modal>
  );
}

/* ─────────── Decision ─────────── */
export function CreateDecisionModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const PHASES = usePhases();
  const [form, setForm] = useState({
    decision: "", madeBy: "",
    date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    context: "", status: "pending", phase: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.decision.trim() || !form.madeBy.trim()) { setError("Decision and decider are required."); return; }
    setSaving(true); setError("");
    try {
      await postJson("/api/decisions", form);
      qc.invalidateQueries({ queryKey: ["decisions"] });
      setForm({ decision: "", madeBy: "", date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric" }), context: "", status: "pending", phase: "" });
      onClose();
    } catch (err) { setError(err instanceof Error ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Log a decision">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Decision {req}</label>
          <input value={form.decision} onChange={(e) => set("decision", e.target.value)} className={inputCls} placeholder="e.g. Hold demo account in test env, not prod" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Decider {req}</label>
            <input value={form.madeBy} onChange={(e) => set("madeBy", e.target.value)} className={inputCls} placeholder="Stephen / ELT" />
          </div>
          <div>
            <label className={labelCls}>Date</label>
            <input value={form.date} onChange={(e) => set("date", e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Context</label>
          <textarea value={form.context} onChange={(e) => set("context", e.target.value)} rows={3} className={inputCls} placeholder="Why this call, what alternatives were considered." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Tied to phase</label>
            <select value={form.phase} onChange={(e) => set("phase", e.target.value)} className={inputCls}>
              <option value="">— General —</option>
              {PHASES.map((p) => (
                <option key={p.id} value={p.name}>{p.name}{p.isGate ? " (gate)" : ""}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Status</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
              <option value="pending">Pending</option>
              <option value="final">Final</option>
            </select>
          </div>
        </div>
        <ErrorBanner error={error} />
        <Actions saving={saving} onClose={onClose} submitLabel="Log decision" />
      </form>
    </Modal>
  );
}
